import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import { Alert, Button, Field, Loading } from '../../shared/ui/components';
import { useSession } from '../session/SessionProvider';
import { AuthLayout } from './AuthLayout';

type Estado = 'pendiente' | 'aprobada' | 'rechazada' | 'vencida';

/**
 * Un hijo sin cuenta entra en su propio celular o tablet: con el QR que le muestra papá o mamá
 * (llega con ?codigo=…), o pidiéndolo con el correo de papá o mamá y esperando su aprobación.
 */
export function ChildDevicePage() {
  const [params] = useSearchParams();
  const code = params.get('codigo');
  const { refresh } = useSession();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [estado, setEstado] = useState<Estado | null>(null);
  const used = useRef(false);

  async function enter() {
    await refresh();
    navigate('/app', { replace: true });
  }

  useEffect(() => {
    if (!code || used.current) return;
    used.current = true;
    api
      .post('/security/hijo/codigo', { codigo: code })
      .then(enter)
      .catch((e: unknown) => setError(errorMessage(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  // Mientras espera, pregunta cada pocos segundos si papá o mamá ya aprobaron.
  useEffect(() => {
    if (!token) return;
    let stop = false;
    const tick = async () => {
      try {
        const r = await api.post<{ estado: Estado }>('/security/hijo/solicitud/estado', { token });
        if (stop) return;
        setEstado(r.estado);
        if (r.estado === 'aprobada') return void enter();
        if (r.estado === 'pendiente') setTimeout(tick, 3000);
      } catch (e) {
        if (!stop) setError(errorMessage(e));
      }
    };
    void tick();
    return () => {
      stop = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const correo = String(form.get('correo')).trim();
    const nombre = String(form.get('nombre')).trim();
    if (!correo || !nombre) {
      setError(!correo ? 'Escribe el correo de tu papá o tu mamá.' : 'Escribe tu nombre.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      setToken((await api.post<{ token: string }>('/security/hijo/solicitud', { correo, nombre })).token);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  const footer = (
    <>
      ¿Tienes tu propia cuenta? <Link to="/entrar">Iniciar sesión</Link>
    </>
  );

  if (code) {
    return (
      <AuthLayout title="Soy hijo o hija" footer={footer}>
        {error ? <Alert>{error}</Alert> : <Loading label="Entrando…" />}
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Soy hijo o hija" footer={footer}>
      {token ? (
        <>
          {error ? <Alert>{error}</Alert> : null}
          {estado === 'rechazada' ? <Alert>Tu papá o tu mamá no lo aprobaron. Pídeles que te ayuden.</Alert> : null}
          {estado === 'vencida' ? (
            <Alert>
              Ya pasó el tiempo y nadie lo aprobó. Revisa que el correo y tu nombre sean los mismos que tienen en KEORA, o pide a tu papá o mamá que
              te muestre el código QR desde su perfil.
            </Alert>
          ) : null}
          {estado === 'pendiente' || estado === null ? (
            <>
              <Loading label="Esperando a tu papá o tu mamá…" />
              <p className="muted">Les llegó un aviso en su KEORA. Cuando lo aprueben, entras solo.</p>
            </>
          ) : (
            <Button type="button" variant="secondary" className="btn--block" onClick={() => { setToken(null); setEstado(null); }}>
              Intentar otra vez
            </Button>
          )}
        </>
      ) : (
        <>
          <p className="muted">Escribe el correo de tu papá o tu mamá y tu nombre. Les llegará un aviso para que te dejen entrar en este aparato.</p>
          <form className="form" onSubmit={onSubmit} noValidate>
            {error ? <Alert>{error}</Alert> : null}
            <Field label="Correo de tu papá o tu mamá" name="correo" type="email" autoComplete="off" required />
            <Field label="Tu nombre" name="nombre" autoComplete="given-name" required />
            <Button type="submit" busy={busy} className="btn--block">
              Pedir permiso
            </Button>
          </form>
          <p className="small muted">Si están juntos, es más rápido: tu papá o tu mamá te muestran un código QR desde su perfil, en Familia.</p>
        </>
      )}
    </AuthLayout>
  );
}
